import { ListItemButton, ListItemIcon } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import { useUpdateTranslation } from "apollo/hooks/mutations";
import { useGetTranslation, useGetTranslations } from "apollo/hooks/queries";
import { Result } from "apollo/utils";
import { useDateLocale } from "core/hooks/useDateLocale";
import { format, fromUnixTime } from "date-fns";
import RSButton, { RSFAB } from "ds_legacy/components/RSButton";
import { SpinnerPage } from "ds_legacy/components/Spinner";
import Tooltip from "ds_legacy/components/Tooltip";
import {
  APP_BACKGROUND,
  CARD_BORDER,
  ERROR_COLOR,
  SUCCESS_COLOR,
  TEXT_DARK_PRIMARY,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  Body,
  Caption,
  Display,
  FONT_SIZE_14,
  Title,
} from "ds_legacy/materials/typography";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { sort } from "jsonabc";
import { debounce } from "lodash";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  DownloadIcon,
  HelpCircleIcon,
  PencilIcon,
  PlusCircleIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Trash2Icon,
  UploadIcon,
} from "lucide-react";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { InputField } from "./fields";
import {
  useCanDeleteTranslation,
  useForm,
  useTranslationsEditor,
} from "./hooks";
import { locales } from "./index";
import {
  AddEntryModal,
  ExportFromSourceModal,
  HelpModal,
  RemoveEntryModal,
} from "./modals";
import {
  DispatchContext,
  TRANSLATION_ACTIONS,
  useTranslationsDispatch,
} from "./translationsHandler";
import {
  LocalesType,
  RepositoryType,
  Search,
  SearchType,
  Selection,
  TranslationType,
  TranslationTypes,
} from "./types";
import {
  applyTagsLogic,
  convertTranslationsToCsv,
  getEmptySelection,
  getOntology,
  isDev,
  isFilteredBySearch,
  isLocaleValue,
  isSelection,
  isTranslationValid,
  searchTags,
  translationSuggestions,
} from "./utils";

export const StatusIndicator = ({
  indicator,
}: {
  indicator: boolean | null;
}) => {
  if (indicator == null) {
    return null;
  }
  return (
    <div
      style={{
        display: "inline-flex",
        fontSize: "1.5rem",
        padding: padding(1.5),
      }}
    >
      {indicator ? (
        <Tooltip title="Your previous export was successful">
          <ThumbsUpIcon size={16} style={{ color: SUCCESS_COLOR }} />
        </Tooltip>
      ) : (
        <Tooltip title="Your previous export failed">
          <ThumbsDownIcon size={16} style={{ color: ERROR_COLOR }} />
        </Tooltip>
      )}
    </div>
  );
};

const SearchBar = ({
  indicator,
  repo,
  search,
  setSearch,
}: {
  indicator: boolean | null;
  repo: RepositoryType;
  search: Search;
  setSearch: Dispatch<SetStateAction<Search>>;
}) => {
  const [, translations] = useGetTranslations(repo);
  const [open, setOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const text = e.target.value;
    setSearch((state) => ({ ...state, text }));
  }

  const debouncedHandleChange = useMemo(() => debounce(handleChange, 350), []);

  useEffect(() => {
    return () => debouncedHandleChange.cancel();
  });

  if (!translations) return null;
  return (
    <>
      <div className="flex max-w-full flex-col">
        <TextField
          defaultValue={search.text}
          id="input_search"
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                style={{ margin: margin(0, 2, 0, 0) }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      id="search_switcher"
                      value={search.type}
                      onChange={() =>
                        setSearch((state) => ({
                          ...state,
                          type:
                            search.type === SearchType.Content
                              ? SearchType.Context
                              : SearchType.Content,
                        }))
                      }
                    />
                  }
                  label={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        width: dp(200),
                      }}
                    >
                      {search.type === SearchType.Content
                        ? "Searching inside translations"
                        : "Searching in context"}
                    </div>
                  }
                  labelPlacement="start"
                />
              </InputAdornment>
            ),
          }}
          label="Search"
          margin="normal"
          placeholder={
            search.type === SearchType.Content
              ? "Search inside translations"
              : "Search translations keys"
          }
          style={{ margin: margin(0), width: "100%" }}
          variant="filled"
          onChange={debouncedHandleChange}
        />
        <HorizontalLayout
          id="action_bar"
          stretched
          style={{ justifyContent: "space-between" }}
        >
          <div>
            {Object.entries(searchTags).map(
              ([key, { label, lang }]: [string, ToType]) => {
                const tag = typeof key == "string" ? parseInt(key) : key;
                return (
                  <Chip
                    key={tag}
                    avatar={<Avatar>{lang}</Avatar>}
                    clickable
                    color={search.tags.includes(tag) ? "primary" : undefined}
                    id={`${lang}-${label}`}
                    label={label}
                    style={{ margin: margin(1) }}
                    variant={search.tags.includes(tag) ? undefined : "outlined"}
                    onClick={() => {
                      if (search.tags.includes(tag))
                        setSearch((prev) => ({
                          ...prev,
                          tags: prev.tags.filter((t) => t !== tag),
                        }));
                      else
                        setSearch((prev) => ({
                          ...prev,
                          tags: prev.tags.concat(tag),
                        }));
                    }}
                  />
                );
              },
            )}
          </div>
          <div className="flex items-center space-x-2">
            <a
              download="translations.csv"
              href={convertTranslationsToCsv(translations)}
            >
              <IconButton size="large">
                <DownloadIcon size={16} />
              </IconButton>
            </a>
            {isDev && (
              <IconButton size="large" onClick={() => setExportOpen(true)}>
                <UploadIcon size={16} />
              </IconButton>
            )}
            <StatusIndicator indicator={indicator} />
            <IconButton size="large" onClick={() => setOpen(true)}>
              <HelpCircleIcon size={16} />
            </IconButton>
          </div>
        </HorizontalLayout>
      </div>
      {open && <HelpModal isOpen setOpen={setOpen} />}
      {exportOpen && (
        <ExportFromSourceModal isOpen repo={repo} setOpen={setExportOpen} />
      )}
    </>
  );
};

function ElementCollapsed({
  config: { label, nestedLevel },
  pathEntry,
  pathKey,
  repo,
  search,
  selection,
  translations,
}: {
  config: { label: string; nestedLevel: number };
  pathEntry: AnyObject;
  pathKey: string;
  repo: RepositoryType;
  search: Search;
  selection: Selection;
  translations: AnyObject;
}) {
  const [open, setOpen] = useState(false);
  const [addModal, setAddModal] = useState(false);

  return (
    <div>
      {addModal && (
        <AddEntryModal
          isOpen={addModal}
          originKey={pathKey}
          repo={repo}
          setOpen={setAddModal}
          translations={translations}
        />
      )}
      <ListItemButton
        key={pathKey}
        divider
        id={pathKey}
        sx={{ pl: `calc(${dp(16)} + ${padding(nestedLevel)})` }}
        onClick={() => setOpen(!open)}
      >
        <ListItemIcon>
          {open ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
        </ListItemIcon>
        <ListItemText primary={label} sx={{ fontSize: FONT_SIZE_14 }} />
        <ListItemSecondaryAction>
          <IconButton size="large" onClick={() => setAddModal(true)}>
            <PlusCircleIcon size={16} />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItemButton>

      <Collapse
        component="div"
        in={open}
        sx={{ width: "100%" }}
        timeout="auto"
        unmountOnExit
      >
        <List component="div" disablePadding sx={{ width: "100%" }}>
          {Object.entries(pathEntry).map(([nestedKey, nestedValue]) => {
            const newKey = `${pathKey}.${nestedKey}`;

            return (
              <TreeElement
                key={newKey}
                data={[newKey, nestedValue]}
                repo={repo}
                search={search}
                selection={selection}
                translations={translations}
              />
            );
          })}
        </List>
      </Collapse>
    </div>
  );
}

function Element({
  config: { label, nestedLevel },
  repo,
  selection,
  translation,
  translationKey,
}: {
  config: { label: string; nestedLevel: number };
  repo: RepositoryType;
  selection: Selection;
  translation: TranslationType;
  translationKey: string;
}) {
  const dispatch = useTranslationsDispatch();
  const [removeModal, setRemoveModal] = useState(false);

  const isSelected = isSelection(selection, translationKey);
  const canDelete = useCanDeleteTranslation({
    createdAt: translation.created_at,
  });

  return (
    <>
      {canDelete && (
        <RemoveEntryModal
          isOpen={removeModal}
          repo={repo}
          setOpen={setRemoveModal}
          value={translation}
          valueKey={translationKey}
        />
      )}
      <ListItemButton
        key={translationKey}
        dense
        divider
        id={translationKey}
        selected={isSelected}
        sx={{ pl: `calc(${dp(16)} + ${padding(nestedLevel)})` }}
        onClick={() =>
          isSelected
            ? dispatch({
                payload: { selection: getEmptySelection() },
                type: TRANSLATION_ACTIONS.SELECT,
              })
            : dispatch({
                payload: { selection: [translationKey, translation] },
                type: TRANSLATION_ACTIONS.SELECT,
              })
        }
      >
        <ListItemIcon>
          <PencilIcon
            size={16}
            style={{ color: TEXT_DARK_PRIMARY, margin: margin(0, 0.5) }}
          />
        </ListItemIcon>
        <ListItemText primary={label} />
        {canDelete && (
          <ListItemSecondaryAction>
            <IconButton
              aria-label="Remove entry"
              data-testid={`${translationKey}-remove-button`}
              size="large"
              onClick={() => setRemoveModal(true)}
            >
              <Trash2Icon size={16} />
            </IconButton>
          </ListItemSecondaryAction>
        )}
      </ListItemButton>
    </>
  );
}

const TreeElement = ({
  data,
  repo,
  search,
  selection,
  translations,
}: {
  data: [string, any];
  repo: RepositoryType;
  search: Search;
  selection: Selection;
  translations: AnyObject;
}) => {
  const [key, value] = data;
  const filtered = useMemo(
    () => isFilteredBySearch(search, { key, value }),
    [search, key, value],
  );

  if (filtered) return null;

  const nestedLevel = (key.match(/\./g) || []).length;
  const label = nestedLevel > 0 ? key.slice(key.lastIndexOf(".") + 1) : key;

  if (isLocaleValue(value)) {
    return (
      <Element
        config={{ label, nestedLevel }}
        repo={repo}
        selection={selection}
        translation={value}
        translationKey={key}
      />
    );
  }

  return (
    <ElementCollapsed
      config={{ label, nestedLevel }}
      pathEntry={value}
      pathKey={key}
      repo={repo}
      search={search}
      selection={selection}
      translations={translations}
    />
  );
};

const TreeView = ({
  repo,
  search,
  selection,
  translations,
}: {
  repo: RepositoryType;
  search: Search;
  selection: Selection;
  translations: AnyObject;
}) => {
  const [modal, setModal] = useState(false);

  return (
    <List
      component="div"
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        overflowX: "scroll",
        width: "100%",
      }}
    >
      <>
        {modal && (
          <AddEntryModal
            isOpen={modal}
            repo={repo}
            setOpen={setModal}
            translations={translations}
          />
        )}
        <div>
          <ListItemButton divider onClick={() => setModal(true)}>
            <ListItemIcon>
              <PlusCircleIcon size={16} />
            </ListItemIcon>
            <ListItemText primary="Add translation in root folder" />
          </ListItemButton>
        </div>
      </>
      {Object.keys(translations).length > 0 ? (
        Object.entries(translations).map((keyValue) => (
          <TreeElement
            key={keyValue.toString()}
            data={keyValue}
            repo={repo}
            search={search}
            selection={selection}
            translations={translations}
          />
        ))
      ) : (
        <SpinnerPage id="state-population" />
      )}
    </List>
  );
};

const PlaceholderComponent = () => (
  <div
    style={{
      alignItems: "center",
      display: "flex",
      flex: "1 1 auto",
      justifyContent: "center",
    }}
  >
    <Display>Select an item from the list to begin</Display>
  </div>
);

function ConnectedModificationView({
  repo,
  selection,
}: {
  repo: RepositoryType;
  selection: Selection;
}) {
  const [id] = selection;
  const [queryProgress, translation] = useGetTranslation(id, repo, {
    fetchPolicy: "network-only",
    skip: !id,
  });
  const translationOverloaded = useMemo(
    () =>
      translation != null
        ? {
            ...translation,
            description: translation.description || "",
            links: translation.links || [],
            tags: translation.tags || [],
          }
        : translation,
    [translation],
  );

  return (
    <div className="flex w-1/2">
      <div
        className="flex max-h-full flex-1 flex-col items-center justify-between overflow-auto border-l-4"
        style={{
          backgroundColor: APP_BACKGROUND,
          borderLeftColor: CARD_BORDER,
        }}
      >
        <Result
          data={translationOverloaded}
          getErrorLabel={() => "Could not load translation"}
          id="acp-translation-get"
          isFlex
          PlaceholderComponent={<PlaceholderComponent />}
          queryProgress={queryProgress}
        >
          {(translationOverloaded) => (
            <ModificationView repo={repo} translation={translationOverloaded} />
          )}
        </Result>
      </div>
    </div>
  );
}

const ModificationView = ({
  repo,
  translation,
}: {
  repo: RepositoryType;
  translation: TranslationType | undefined;
}) => {
  const dispatch = useTranslationsDispatch();
  const toast = useToast();
  const { currentLocale: locale } = useDateLocale();
  const [updateTranslation, queryProgress, resetProgress] =
    useUpdateTranslation(repo);

  const { formState, setFormValue, submit } = useForm(
    Object.keys(locales).reduce(
      (form, key) => ({
        ...form,
        [key]: {
          validate: isTranslationValid(key),
          value: getOntology(translation, key as keyof LocalesType),
        },
      }),
      Object.entries(translation || {})
        .filter(([key]) => key !== "locales")
        .reduce((init, [key, value]) => ({ ...init, [key]: { value } }), {}),
    ),
    () => {
      const { de, en, ...infos } = Object.entries(formState).reduce(
        (acc, [key, { value }]) => ({
          ...acc,
          [key]: value,
        }),
        {
          de: null,
          description: "",
          en: null,
          id: translation ? translation.id : "-1",
          links: null,
          tags: [],
        },
      );

      const newTranslation = applyTagsLogic({
        ...infos,
        locales: {
          de: de || "",
          en: en || "",
        },
      });

      updateTranslation({
        variables: {
          id: translation?.id ?? "-1",
          input: newTranslation,
        },
      }).then(() => {
        dispatch({
          payload: newTranslation,
          type: TRANSLATION_ACTIONS.UPDATE,
        });
        toast({
          color: "primary",
          message: "Translations saved ! Don't forget to export !",
        });
        setTimeout(() => resetProgress(), 3000);
      });
    },
    [translation?.id],
  );

  useEffect(() => {
    Object.keys(locales).forEach((key) =>
      setFormValue(key, getOntology(translation, key as keyof LocalesType)),
    );
  }, [translation]);

  if (!translation?.id) return <PlaceholderComponent />;

  return (
    <div className="flex w-full flex-1 flex-col items-center pb-6">
      <VerticalLayout>
        <HorizontalLayout aligned className="title">
          <Title>You have selected:</Title>
          <RSButton
            color="primary"
            id="id"
            loading="na"
            variant="text"
            onClick={() => {
              if (!document.body) {
                return;
              }
              const xClip = document.createElement("input");
              xClip.setAttribute("id", "clipboard");
              xClip.setAttribute("value", `translations.${translation.id}`);
              document.body.appendChild(xClip);
              xClip.select();
              document.execCommand("copy");
              document.body.removeChild(xClip);
              toast({
                color: "primary",
                message: "Translation path copied to clipboard!",
              });
            }}
          >
            <Tooltip title="Copy translation path to clipboard">
              <HorizontalLayout aligned>
                <Body style={{ textTransform: "initial" }}>
                  {translation.id}
                </Body>
                <CopyIcon size={16} style={{ color: TEXT_DARK_PRIMARY }} />
              </HorizontalLayout>
            </Tooltip>
          </RSButton>
        </HorizontalLayout>
        <HorizontalLayout aligned style={{ justifyContent: "center" }}>
          {translation.created_at && (
            <Caption>
              <b>Created on:</b>&nbsp;
              {format(
                fromUnixTime(translation.created_at),
                "dd/MM/yy - hh:mm",
                { locale },
              )}
            </Caption>
          )}
          {translation.updated_at && (
            <Caption>
              <b>Last update on:</b>&nbsp;
              {format(
                fromUnixTime(translation.updated_at),
                "dd/MM/yy - hh:mm",
                { locale },
              )}
            </Caption>
          )}
        </HorizontalLayout>
      </VerticalLayout>
      <div className="w-[90%]">
        <InputField
          key="Description"
          error={formState.description.error}
          label="Description"
          placeholder="Example: 'Explanation text of confirmation modal when editing search'"
          rows="1"
          setValue={(value) => setFormValue("description", value)}
          value={formState.description.value}
        />
        {Object.keys(locales).map((key) => (
          <InputField
            key={locales[key]}
            error={formState[key].error}
            id={key}
            isRequired={key === "en"}
            label={locales[key]}
            setTags={(tags) => setFormValue("tags", tags)}
            setValue={(value) => setFormValue(key, value)}
            suggestions={translationSuggestions}
            tags={
              Array.isArray(formState.tags.value) ? formState.tags.value : []
            }
            value={formState[key].value}
            onReset={
              formState[key].value !==
              getOntology(translation, key as keyof LocalesType)
                ? () =>
                    setFormValue(
                      key,
                      getOntology(translation, key as keyof LocalesType),
                    )
                : undefined
            }
          />
        ))}
      </div>
      <div className="mb-0">
        <RSButton
          color="primary"
          id="save"
          loading={queryProgress}
          style={{ margin: margin(1), width: dp(160) }}
          variant="contained"
          onClick={submit}
        >
          Save
        </RSButton>
      </div>
    </div>
  );
};

function insertTop(
  object: AnyObject,
  key: string,
  entry: AnyObject,
): AnyObject {
  if (
    !Object.keys(object).length ||
    Object.values(object).every((objEntry) => isLocaleValue(objEntry))
  )
    return {
      [key]: entry,
      ...object,
    };

  const [targetKey] =
    Object.entries(object).find(([, objEntry]) => isLocaleValue(objEntry)) ||
    [];

  if (!targetKey) return { ...object, [key]: entry };

  return Object.entries(object).reduce((obj, [objKey, objEntry]) => {
    if (objKey === targetKey)
      return {
        ...obj,
        [key]: entry,
        [objKey]: objEntry,
      };
    return { ...obj, [objKey]: objEntry };
  }, {});
}

function nestOrdering(object: AnyObject): AnyObject {
  if (isLocaleValue(object)) return object;

  return Object.entries(object).reduce((obj, [key, entry]) => {
    if (!isLocaleValue(entry)) return insertTop(obj, key, nestOrdering(entry));

    return { ...obj, [key]: entry };
  }, {});
}

export const TranslationsPanel = ({
  indicator,
  onExport,
  repo,
  translationsArray,
}: {
  indicator: boolean | null;
  onExport: (translations: ToType) => void;
  repo: RepositoryType;
  translationsArray: Readonly<TranslationTypes> | undefined;
}) => {
  const [search, setSearch] = useState<Search>({
    tags: [],
    text: "",
    type: SearchType.Content,
  });
  const { dispatch, exportTranslations, selection, translations } =
    useTranslationsEditor({
      onExport,
      translationsArray,
    });

  const orderedTranslations = useMemo(() => {
    const alphaOrdered = JSON.parse(
      sort(JSON.stringify(translations.toJS(), null, 2), true),
    );

    return nestOrdering(alphaOrdered);
  }, [translations]);

  return (
    <DispatchContext.Provider value={dispatch}>
      <div className="flex h-full max-h-[calc(100vh-48px)] w-full flex-1 flex-col overflow-hidden">
        <SearchBar
          indicator={indicator}
          repo={repo}
          search={search}
          setSearch={setSearch}
        />

        <div className="flex h-[calc(100vh-104px-48px)] w-full flex-1">
          <TreeView
            repo={repo}
            search={search}
            selection={selection}
            translations={orderedTranslations}
          />
          <ConnectedModificationView repo={repo} selection={selection} />
        </div>
        <div className="fixed bottom-0 right-0 m-2">
          <RSFAB
            color="secondary"
            variant="extended"
            onClick={exportTranslations}
          >
            <b style={{ width: dp(150) }}>Export</b>
          </RSFAB>
        </div>
      </div>
    </DispatchContext.Provider>
  );
};
